/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';
import { t } from 'i18next';

// Component imports
import { Avatar, Button, Col, Divider, List, Row, Space, Tooltip, Typography } from 'antd';
import { RobotOutlined, UserOutlined, WarningOutlined, MoreOutlined } from '@ant-design/icons';
import { AgentAcceptIcon } from '../../Icons/AgentAcceptIcon';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import Highlighter from 'react-highlight-words';
import { ThumbsUpIconTwoTone } from '../../Icons/ThumbsUpIcon';
import { ThumbsDownIconTwoTone } from '../../Icons/ThumbsDownIcon';
import { TabIndicatorIcon } from '../../Icons/TabIndicatorIcon';

// Misc imports
import { LIVECHAT_EVENT_TYPES } from '../../../constants/liveChat';
import { removeMarkdown } from '../../../utils/string/removeMarkdown';
import { isString } from '../../../utils/is/isString';
import { ANALYTIC_TAGS_RECOMMENDATIONS } from '../../../constants/flowGraph';
import { CHAT_SESSION_MESSAGE_TYPES } from '../../../constants/main';
import { NAV_BOT } from '../../../constants/navigation';
import { useBotLanguage } from '../../../utils/hooks/useBotLanguage';
import { MARKDOWN_IMG_URL_REGEX } from '../../../constants/regex';
import { isEmptyArray } from '../../../utils/is/isEmptyArray';
import formatTimestamp from '../../../utils/misc/formatTimestamp';
import replaceUrlParams from '../../../utils/string/replaceUrlParams';
import { isEmptyObject } from '../../../utils/is/isEmptyObject';

const formatSubscriberName = (name) => {
  if (!name) {
    return t('Anonymous');
  }

  if (name.length > 1) {
    return name[0].toUpperCase() + name.substring(1);
  }

  return name;
};

const ChatSession = forwardRef(({
  session,
  searchTerm,
  id,
  loading,
  className,
  wrapperClassName,
  handleSetSelectedChatSessionMessage,
  isInteractionMetadataEnabled = false,
  wrapperStyle,
  isHistory,
  timezone,
  missedMessageIds = [],
  onOpenExternalComponent = () => null,
  onMissedQuestionMessageVisibilityChange = () => null,
  sessionTenantId,
  ...rest
}, ref) => {

  const { orgId } = useParams();
  const { primaryBotLanguage, currentBotLanguage } = useBotLanguage();
  const [hasScrolledIntoViewOnce, setHasScrollIntoViewOnce] = useState(false);
  const [selectedChatSessionMessage, setSelectedChatSessionMessage] = useState({});
  const [arrowTop, setArrowTop] = useState(null);
  const scrollContainerRef = useRef(null);
  const SVG_CONNECTOR_Y_OFFSET = 28;

  const handleChatSessionMessageSelection = (message) => {
    setSelectedChatSessionMessage(message);
    handleSetSelectedChatSessionMessage(message);
  };

  const missedQuestionDetailsButtonRefs = useRef(new Map());

  const handleMissedQuestionDetailsButtonClick = (messageId) => {
    if (messageId) {
      onOpenExternalComponent(messageId);
    }
  };

  useImperativeHandle(ref, () => ({
    ...(!isEmptyArray?.(missedMessageIds) && { navigateToMissedQuestionMessage: handleOnNavigateToMissedQuestion })
  }));

  const handleOnNavigateToMissedQuestion = (incomingIndex) => {
    let nextIndex = incomingIndex === missedMessageIds?.length ? 0 : incomingIndex < 0 ? missedMessageIds?.length - 1 : incomingIndex;
    if(missedMessageIds?.length === 1) {
      nextIndex = 0;
    }

    setTimeout(() => {
      const missedMessageElement = document.getElementById(missedMessageIds[nextIndex]);
      if (missedMessageElement) {
        missedMessageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  useEffect(() => {
    if(!isEmptyObject(selectedChatSessionMessage) && scrollContainerRef?.current) {
      const selectedLi = scrollContainerRef.current.querySelector(`li[data-key="${selectedChatSessionMessage.id}"]`);

      if (selectedLi) {
        const messageTop = selectedLi.offsetTop;
        const messageHeight = selectedLi.clientHeight;
        const messageCenterY = messageTop + (messageHeight / 2);
        const newTop = messageCenterY - SVG_CONNECTOR_Y_OFFSET;
        setArrowTop(newTop);
      }
    } else {
      setArrowTop(null);
    }
  }, [selectedChatSessionMessage]);

  useEffect(() => {
    if (!isEmptyArray(missedMessageIds)) {
      if(isEmptyArray(Array.from(missedQuestionDetailsButtonRefs?.current?.values()))) {
        missedMessageIds.forEach((messageId) => {
          if (!missedQuestionDetailsButtonRefs?.current?.has(messageId)) {
            missedQuestionDetailsButtonRefs.current?.set(messageId, React.createRef());
          }
        });
      }

      if(!hasScrolledIntoViewOnce) {
        setTimeout(() => {
          const missedMessageElement = document.getElementById(missedMessageIds[0]);
          if (missedMessageElement) {
            missedMessageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          setHasScrollIntoViewOnce(true);
        }, 300);
      }

      const observerCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const visibleMissedMessageIndex = missedMessageIds.findIndex(id => id === entry.target.id);
            if (visibleMissedMessageIndex !== -1) {
              onMissedQuestionMessageVisibilityChange(visibleMissedMessageIndex);
            }
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, {
        root: null,
        threshold: 0.5
      });

      missedQuestionDetailsButtonRefs.current.forEach((ref) => {
        if (ref.current) {
          observer.observe(ref.current);
        }
      });

      return () => {
        observer.disconnect();
      };
    }

  }, [missedMessageIds, hasScrolledIntoViewOnce]);

  /**
   * Show thumbs up/down feedback if it exists under the chat bubble
   * @param {Object} message The message object
   */
  const renderThumbsFeedback = (message) => {
    const latestThumbRating = (message.tags || []).find(tag => tag === ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_POSITIVE || tag === ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_NEGATIVE);

    if (latestThumbRating) {
      return (
        <Row
          wrap={false}
          align="middle"
        >
          {
            latestThumbRating === ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_POSITIVE ? (
              <Button
                size="small"
                shape="circle"
                className="bordered-lg pointer-events-none"
                icon={<ThumbsUpIconTwoTone className="p-1" />}
                style={{ top: -12, left: -8 }}
              />
            ) : (
              <Button
                danger
                size="small"
                shape="circle"
                className="bordered-lg pointer-events-none"
                icon={<ThumbsDownIconTwoTone className="p-1" />}
                style={{ top: -12, left: -8 }}
              />
            )
          }
        </Row>
      );
    }
  };

  /**
   * Helper function for constructing tooltip text and link for messages with intents
   * @param {Object} message The message object
   * @returns {{tooltipText: string, tooltipLink: string}}
   */
  const getTooltipData = (message) => {
    const isFallback = message?.category === 'FALLBACK';

    const tooltipText = isFallback
      ? message?.intentName
      : (message?.intentMatchingName?.[currentBotLanguage] || message?.intentMatchingName?.[primaryBotLanguage]);

    const tooltipLink = isFallback
      ? '?view=behavior&tab=fallbacks'
      : `?view=behavior&tab=automated-answers&edit=${message?.intentName}`;

    return { tooltipText, tooltipLink };
  };

  /**
   * Render the message content that will appear in the chat bubble
   * @param {Object} message The message object
   */
  const renderMessageContent = (message) => {
    const messageToRender = isString(message) ? message : message.message;
    const isImageMarkdown = searchTerm ? MARKDOWN_IMG_URL_REGEX.test(messageToRender) : false;

    return (
      <Fragment>
        {
          searchTerm && !isImageMarkdown? (
            <Highlighter
              highlightClassName="bg-highlight-alt p-0"
              searchWords={searchTerm.split()}
              autoEscape={true}
              textToHighlight={removeMarkdown(messageToRender)}
            />
          ) : (
            <ReactMarkdown
              skipHtml
              allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'img', 'a']}
              unwrapDisallowed
              linkTarget={'_blank'}
            >
              {messageToRender}
            </ReactMarkdown>
          )
        }
      </Fragment>
    );
  };

  return (
    <Fragment>
      <div
        wrap={false}
        className={classNames(
          'w-100 h-100 bg-layout position-relative',
          selectedChatSessionMessage?.id ? 'overflow-y-hidden' : 'overflow-y-auto',
          wrapperClassName
        )}
        style={wrapperStyle}
        ref={scrollContainerRef}
      >
        <div
          hidden={arrowTop === null && !selectedChatSessionMessage?.id}
          className="position-absolute z-index-dropdown right-0 cursor-pointer"
          onClick={() => handleChatSessionMessageSelection({})}
          style={{
            top: `${arrowTop}px`
          }}>
          <TabIndicatorIcon />
        </div>
        <Divider className="system-message-divider text-primary fs-sm mt-8">
          {t('Conversation Started')}
        </Divider>

        <List
          id={id}
          className="message-list px-3"
          dataSource={session}
          locale={{ emptyText: t('This is the start of the conversation') }}
          rowKey="id"
          loading={loading}
          renderItem={(message, index) => {
            if (
              message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_ACCEPTED
               || message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED
                || message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_CLOSED
            ) {

              let systemMessage = '';

              switch(message.liveChatEventType) {
                case LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_ACCEPTED:
                  systemMessage = 'LiveChat Started';
                  break;
                case LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED:
                  systemMessage = 'LiveChat Ended';
                  break;
                case LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_CLOSED:
                  systemMessage = 'LiveChat Cancelled';
                  break;
                default:
                  break;
              }

              return (
                <li
                  key={message.id}
                  data-key={message.id}
                  className="mt-10 max-w-100"
                >
                  <Divider className="system-message-divider text-primary fs-sm">
                    {systemMessage}
                  </Divider>
                </li>
              );
            }

            let hasNext = false;
            const nextMessage = index === 0 ? null : session[index-1];
            const isText = nextMessage?.type === CHAT_SESSION_MESSAGE_TYPES.TEXT;

            if (isText && nextMessage && (message.subscriberHandle ?? 'bot') === (nextMessage.subscriberHandle ?? 'bot') && moment(nextMessage.date).diff(message.date, 'seconds') < 300) {
              hasNext = true;
            }

            if (index < session.length - 1) {
              const previousMessage = session[index+1];
              const currentMessageType = message.type;
              if (previousMessage?.type === currentMessageType) {
                if ((message.subscriberHandle ?? 'bot') === (previousMessage.subscriberHandle ?? 'bot') && moment(message.date).diff(previousMessage.date, 'seconds') < 300) {
                  return <li
                    key={message.id}
                    data-key={message.id}
                  >
                    <Row
                      wrap={false}
                      align="middle"
                      gutter={8}
                      className={classNames({
                        'mid': hasNext,
                        'last': !hasNext,
                        'livechat-message-incoming': message.liveChatSender === 'END_USER' || (message.subscriberHandle && !message.liveChatSender),
                        'livechat-message-outgoing': !message.liveChatSender || message.liveChatSender !== 'END_USER',
                        'history': isHistory
                      })}
                    >
                      <Col
                        flex="0 0 44px"
                        className="hidden-timestamp"
                      >
                        <Typography.Text
                          className="text-muted fs-xs d-none"
                        >
                          {formatTimestamp(message.date, true, timezone)}
                        </Typography.Text>
                      </Col>

                      <Col flex="auto">
                        <Row
                          className={classNames('message-bubble', {
                            'history': isHistory
                          })}
                        >
                          <Typography.Text
                            onMouseOver={({ target }) => {
                              const wrapperElement = target.closest('li');
                              if (wrapperElement) {
                                wrapperElement?.querySelector('.hidden-timestamp span')?.classList?.replace('d-none', 'd-inline');
                              }
                            }}
                            onMouseLeave={({ target }) => {
                              const wrapperElement = target.closest('li');
                              if (wrapperElement) {
                                wrapperElement?.querySelector('.hidden-timestamp span')?.classList?.replace('d-inline', 'd-none');
                              }
                            }}
                          >
                            {renderMessageContent(message)}
                          </Typography.Text>
                        </Row>

                        {renderThumbsFeedback(message)}
                      </Col>
                    </Row>
                  </li>;
                }
              }
            }

            const { tooltipText, tooltipLink } = getTooltipData(message);

            return (
              <li
                key={message.id}
                data-key={message.id}
                className={`
                  mt-4 mb-2 px-2 py-4 border-radius-lg bordered
                  ${selectedChatSessionMessage?.id === message.id ? 'border-color-primary shadow-lg bg-primary-light w-90' : 'border-color-transparent'} 
                  ${isInteractionMetadataEnabled && message.isUserInteraction && message.subscriberHandle && !message.liveChatSender ? `cursor-pointer ${selectedChatSessionMessage?.id !== message.id ? `interaction-metadata-message` : ''}` : ''}
                  `}
              >
                <Row
                  wrap={false}
                  align="top"
                  gutter={8}
                  className={classNames({
                    'first': hasNext,
                    'livechat-message-incoming': message.liveChatSender === 'END_USER' || (message.subscriberHandle && !message.liveChatSender),
                    'livechat-message-outgoing': !(message.liveChatSender === 'END_USER' || (message.subscriberHandle && !message.liveChatSender)),
                    'history': isHistory
                  })}
                  onClick={() => {
                    if(!isInteractionMetadataEnabled) {
                      return;
                    }

                    if(!message.isUserInteraction || (message.subscriberHandle && message.liveChatSender)) {
                      return;
                    }

                    if(selectedChatSessionMessage?.id === message?.id) {
                      handleChatSessionMessageSelection({});
                    }

                    if(selectedChatSessionMessage?.id !== message?.id) {
                      handleChatSessionMessageSelection(message);
                    }

                  }}
                >
                  <Col flex="0 0 44px">
                    <Avatar
                      size={36}
                      icon={
                        message.liveChatSender === 'AGENT'
                          ? (<AgentAcceptIcon/>)
                          : (message.liveChatSender === message.subscriberHandle ? <RobotOutlined /> : <UserOutlined />)
                      }
                      style={{ verticalAlign: -40 }}
                    />
                  </Col>

                  <Col flex="auto">
                    <Row
                      wrap={false}
                      align="middle"
                      className="fs-sm fw-500 mb-1"
                    >
                      {formatSubscriberName(message.subscriberName)}
                      <span className="text-muted mx-2">&#183;</span>
                      <span className="text-muted fw-normal fs-xs">
                        {formatTimestamp(message.date, true, timezone)}
                      </span>
                    </Row>

                    <Row
                      id={message?.id}
                      className={classNames('message-bubble', {
                        'history': isHistory,
                        'missed-question-message': !isEmptyArray(missedMessageIds) && missedMessageIds?.includes(message.id)
                      })}
                    >
                      <Tooltip title={
                        message?.intentName && (message?.type === 'TEXT') && tooltipText ? (
                          <Link
                            to={replaceUrlParams(NAV_BOT, { tenantId: sessionTenantId, orgId }) + tooltipLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {tooltipText}
                          </Link>
                        ) : null
                      }>
                        <Typography.Text>
                          {renderMessageContent(message)}
                        </Typography.Text>
                      </Tooltip>

                      {!isEmptyArray(missedMessageIds) && missedMessageIds?.includes(message.id) && (
                        <div className="ml-2 mt-2 d-flex align-items-start">
                          <Space direction="horizontal">
                            <Button
                              ref={missedQuestionDetailsButtonRefs?.current?.get(message.id)}
                              style={{
                                width: '22px',
                                height: '22px'
                              }}
                              id={message?.id}
                              type="primary"
                              icon={<MoreOutlined />}
                              onClick={() => handleMissedQuestionDetailsButtonClick(message.id)}
                            />
                            <Tooltip title={t('Missed Question')}>
                              <Typography.Text className="text-warning fs-base">
                                <WarningOutlined />
                              </Typography.Text>
                            </Tooltip>
                          </Space>
                        </div>
                      )}
                    </Row>

                    {renderThumbsFeedback(message)}
                  </Col>
                </Row>
              </li>
            );
          }}
          {...rest}
        />
      </div>
    </Fragment>
  );
});

ChatSession.propTypes = {
  session: PropTypes.array,
  searchTerm: PropTypes.string,
  missedMessageIds: PropTypes.array,
  id: PropTypes.string,
  loading: PropTypes.bool,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  wrapperStyle: PropTypes.object,
  isHistory: PropTypes.bool,
  timezone: PropTypes.string,
  onOpenExternalComponent: PropTypes.func,
  sessionTenantId: PropTypes.string,
  onMissedQuestionMessageVisibilityChange: PropTypes.func,
  handleSetSelectedChatSessionMessage: PropTypes.func,
  isInteractionMetadataEnabled: PropTypes.bool
};

export default ChatSession;
